import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Stack } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  createBillingInvoice,
  createManualBankTransferInvoice,
  createSystemStripeCheckoutSession,
  getBillingInvoices,
} from '../api/billingPortalApi';
import { fetchCustomers } from '../api/authApi';
import { getStripeStatus } from '../api/stripeApi';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { IconButton } from '../components/ui/IconButton';
import { getInvoiceDisplayNumber } from '../billing/externalInvoiceDisplay';
import { stripeOnlinePaymentControlsFromStripeStatus } from '../billing/stripeOnlinePaymentUi';
import { getInvoiceStatusLabel, getProviderLabel } from '../utils/statusLabels';

const INVOICE_STATUSES = ['draft', 'open', 'pending', 'paid', 'failed', 'void'];
const PROVIDERS = ['manual', 'stripe', 'system', 'external_accounting'];
const PAYABLE_STATUSES = ['draft', 'open', 'pending', 'failed'];

export function BillingInvoicesPage() {
  const { t } = useTranslation(['billing', 'common', 'validation', 'statuses', 'stripe']);
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('');
  const [provider, setProvider] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [overdue, setOverdue] = useState('');
  const [creating, setCreating] = useState(false);
  const [checkoutBusyId, setCheckoutBusyId] = useState(null);
  const [stripeStatus, setStripeStatus] = useState(null);

  const emptyValue = t('common:empty_value');
  const recordCount = invoices.length;
  const stripeUi = stripeOnlinePaymentControlsFromStripeStatus(stripeStatus);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (status) params.status = status;
    if (provider) params.provider = provider;
    if (customerId) params.customer_id = customerId;
    if (overdue === 'yes') params.overdue = 1;

    getBillingInvoices(params)
      .then((response) => setInvoices(response.data || []))
      .catch(() => setError(t('billing:load_invoices_failed')))
      .finally(() => setLoading(false));
  }, [status, provider, customerId, overdue, t]);

  useEffect(() => {
    load();
    fetchCustomers().then((r) => setCustomers(r.data || [])).catch(() => setCustomers([]));
    getStripeStatus().then(setStripeStatus).catch(() => setStripeStatus(null));
  }, [load]);

  const handleCreateManual = async () => {
    if (!customerId) {
      setError(t('validation:select_customer_for_invoice'));
      return;
    }
    setCreating(true);
    setError('');
    try {
      const invoice = await createBillingInvoice({
        customer_id: Number(customerId),
        payment_method: 'manual_bank_transfer',
        lines: [{ description: t('billing:manual_invoice_line'), quantity: 1, unit_amount: 100 }],
      });
      navigate(`/billing/invoices/${invoice.id}`);
    } catch {
      setError(t('billing:create_invoice_failed'));
    } finally {
      setCreating(false);
    }
  };

  const handleCreateBankTransfer = async () => {
    if (!customerId) {
      setError(t('validation:select_customer_for_invoice'));
      return;
    }
    setCreating(true);
    setError('');
    setMessage('');
    try {
      const data = await createManualBankTransferInvoice({
        customer_id: Number(customerId),
        generate_pdf: true,
      });
      const invoice = data.invoice || data.resource || data;
      setMessage(t('billing:manualBankTransfer.created'));
      if (invoice?.id) {
        navigate(`/billing/invoices/${invoice.id}`);
      } else {
        load();
      }
    } catch {
      setError(t('billing:manualBankTransfer.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  const handleCheckout = useCallback(async (invoiceId) => {
    setCheckoutBusyId(invoiceId);
    setError('');
    setMessage('');
    try {
      const session = await createSystemStripeCheckoutSession(invoiceId);
      if (session.status === 'disabled') {
        setError(t('stripe:errors.checkoutDisabled'));
        return;
      }
      const url = session.checkout_url || session.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
        setMessage(t('stripe:messages.checkoutSessionCreated'));
        return;
      }
      setError(session.message || t('billing:stripe_checkout_failed'));
    } catch {
      setError(t('billing:stripe_checkout_failed'));
    } finally {
      setCheckoutBusyId(null);
    }
  }, [t]);

  const columns = useMemo(
    () => [
      {
        id: 'number',
        label: t('billing:number'),
        render: (invoice) => getInvoiceDisplayNumber(invoice, emptyValue),
      },
      {
        id: 'customer',
        label: t('billing:customer'),
        render: (invoice) => invoice.customer?.name || invoice.customer_id || emptyValue,
      },
      {
        id: 'tenant',
        label: t('billing:tenant'),
        render: (invoice) => invoice.tenant?.name || emptyValue,
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (invoice) => (
          invoice.display_status || invoice.status
            ? (
              <StatusBadge
                status={invoice.display_status || invoice.status}
                category="invoice"
                size="Sm"
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'payment_method',
        label: t('billing:payment_method'),
        render: (invoice) => {
          const badges = [];
          if (['manual_bank_transfer', 'bank_transfer'].includes(invoice.payment_method)) {
            badges.push(
              <StatusBadge
                key="bank"
                status="manual_bank_transfer"
                category="generic"
                apiLabel={t('billing:payment_methods.manual_bank_transfer')}
                size="Sm"
                tone="Neutral"
              />,
            );
          }
          if (['stripe_checkout', 'stripe', 'card'].includes(invoice.payment_method)) {
            badges.push(
              <StatusBadge
                key="stripe"
                status="stripe_checkout"
                category="generic"
                apiLabel={t('billing:payment_methods.stripe_checkout')}
                size="Sm"
                tone="Info"
              />,
            );
          }
          if (invoice.payment_qr_status === 'generated') {
            badges.push(
              <StatusBadge
                key="qr"
                status="generated"
                category="generic"
                apiLabel={t('billing:invoices.qr.badgeAvailable')}
                size="Sm"
                tone="Success"
              />,
            );
          }
          if (['hidden_stripe_checkout', 'hidden_paid_stripe', 'hidden_stripe'].includes(invoice.payment_qr_status)) {
            badges.push(
              <StatusBadge
                key="qr-hidden"
                status="hidden"
                category="generic"
                apiLabel={t('billing:invoices.qr.badgeHidden')}
                size="Sm"
                tone="Neutral"
              />,
            );
          }
          if (badges.length === 0) {
            return emptyValue;
          }
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {badges}
            </Stack>
          );
        },
      },
      {
        id: 'total',
        label: t('billing:total'),
        render: (invoice) => `${invoice.total_amount} ${invoice.currency}`,
      },
      {
        id: 'due',
        label: t('billing:due'),
        render: (invoice) => (
          invoice.amount_due === 0 || invoice.amount_due
            ? invoice.amount_due
            : emptyValue
        ),
      },
      {
        id: 'provider',
        label: t('billing:provider'),
        render: (invoice) => (
          invoice.provider
            ? <StatusBadge status={invoice.provider} category="provider" size="Sm" />
            : emptyValue
        ),
      },
      {
        id: 'issued',
        label: t('billing:issued'),
        render: (invoice) => (
          invoice.issued_at
            ? new Date(invoice.issued_at).toLocaleDateString()
            : emptyValue
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        render: (invoice) => (
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            {stripeUi.showCreateCheckoutCta
              && PAYABLE_STATUSES.includes(invoice.status)
              && invoice.provider === 'stripe' && (
              <Button
                variant="Ghost"
                loading={checkoutBusyId === invoice.id}
                disabled={checkoutBusyId === invoice.id}
                onClick={(event) => {
                  event.stopPropagation();
                  handleCheckout(invoice.id);
                }}
                sx={{ minHeight: 32, px: 1 }}
              >
                {t('stripe:actions.createCheckout')}
              </Button>
            )}
            <IconButton
              size="Sm"
              tone="Neutral"
              aria-label={t('billing:detail')}
              onClick={(event) => {
                event.stopPropagation();
                navigate(`/billing/invoices/${invoice.id}`);
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [checkoutBusyId, emptyValue, handleCheckout, navigate, stripeUi.showCreateCheckoutCta, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('billing:invoices_title')}
        subtitle={t('billing:invoices_subtitle')}
        actions={(
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Button variant="Secondary" component={RouterLink} to="/billing">
              {t('common:dashboard')}
            </Button>
            <Button
              variant="Accent"
              loading={creating}
              disabled={creating || !customerId}
              onClick={handleCreateBankTransfer}
            >
              {creating ? t('billing:creating') : t('billing:manualBankTransfer.create')}
            </Button>
            <Button
              variant="Secondary"
              disabled={creating || !customerId}
              onClick={handleCreateManual}
            >
              {t('billing:create_manual_invoice')}
            </Button>
          </Stack>
        )}
      />

      <FilterBar type="Simple">
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {INVOICE_STATUSES.map((invoiceStatus) => (
            <FilterSelectOption key={invoiceStatus} value={invoiceStatus}>
              {getInvoiceStatusLabel(invoiceStatus)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('billing:provider')}
          hideLabel
          value={provider}
          onChange={(e) => setProvider(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {PROVIDERS.map((providerValue) => (
            <FilterSelectOption key={providerValue} value={providerValue}>
              {getProviderLabel(providerValue)}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('billing:customer')}
          hideLabel
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          minWidth={180}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {customers.map((customer) => (
            <FilterSelectOption key={customer.id} value={String(customer.id)}>
              {customer.name}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('billing:overdue')}
          hideLabel
          value={overdue}
          onChange={(e) => setOverdue(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          <FilterSelectOption value="yes">{t('common:yes')}</FilterSelectOption>
        </FilterSelect>
      </FilterBar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('billing:invoices_title')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('billing:invoices.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={invoices}
            getRowKey={(invoice) => invoice.id}
            emptyMessage={t('billing:no_invoices')}
            onRowClick={(invoice) => {
              if (invoice.id) {
                navigate(`/billing/invoices/${invoice.id}`);
              }
            }}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>
    </Box>
  );
}
